import {
  AccountData,
  FollowerMembershipState,
  FollowerStatus,
  MembershipPlan,
  SelectRecipientGroupType,
  SelectRecipientType,
} from "@markit/common.types";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../../redux/slices/accountSlice";
import { useCallback, useMemo, useState } from "react";
import filter from "lodash.filter";
import SearchBoxContainer from "../../../Containers/SearchBoxContainer";
import { SelectRecipientsSharedProps } from "./SelectRecipientsScreens";
import { useLoadUserFollowList } from "../../../../hooks/useLoadUserFollowList";
import { useOnMount } from "../../../../hooks/useOnMount";
import { MassTextsPersonItem } from "../Items/MassTextsPersonItem";
import { EmptySearchState } from "../../../EmptyStates/EmptySearchState";
import { CircularProgress } from "@mui/material";
import { HorizontalDivider } from "../../../Dividers/HorizontalDivider";
import { MassTextsActionItem } from "../Items/MassTextsActionItem";
import { Icon } from "@iconify/react";
import { Colors } from "../../../../utils/colors";
import CustomCheckbox from "../../../CustomCheckbox";
import { addOrFilterRecipients } from "@markit/common.utils";
import VirtuosoList from "../../../Lists/VirtuosoList";

type SelectRecipientsFollowersProps = SelectRecipientsSharedProps & {
  selectedType: SelectRecipientGroupType;
};

const SelectRecipientsFollowers = (props: SelectRecipientsFollowersProps) => {
  const {
    selectedType,
    unsavedRecipients,
    setUnsavedRecipients,
    setProfileSelected,
    excludingMode,
    displayAllFollowers,
  } = props;
  const { accountData, followersData, loadedAllFollowers } =
    useSelector(getAccountState).account;
  const [searchTerm, setSearchTerm] = useState("");

  const membershipPlan: MembershipPlan | undefined = useMemo(
    () =>
      selectedType.type === SelectRecipientType.MEMBERSHIPS
        ? (selectedType.selectedItem as MembershipPlan)
        : undefined,
    [selectedType.type, selectedType.selectedItem]
  );

  const {
    fetchedUserData: fetchedFollowerData,
    loadUsers: loadFollowerUsers,
    loadMoreUsers: loadMoreFollowerUsers,
    isLoading: isFetchedFollowersLoading,
    isFinished: isFinishedFollowers,
    loadSearchResultsThrottled,
  } = useLoadUserFollowList({
    userId: accountData.uid,
    followListType: "Followers",
    followerStatus: displayAllFollowers ? undefined : FollowerStatus.SUBSCRIBED,
    windowSize: 20,
    membershipPlanId: membershipPlan ? membershipPlan.id : undefined,
  });

  useOnMount(() => {
    loadFollowerUsers();
  });

  const containsUser = useCallback((user: AccountData, query: string) => {
    return user.fullName.toLowerCase().includes(query.toLowerCase());
  }, []);

  const handleSearch = (text: string) => {
    if (text !== "") {
      loadSearchResultsThrottled(text.toLowerCase());
    }
    setSearchTerm(text);
  };

  const activeFollowersData = useMemo(
    () =>
      membershipPlan
        ? followersData.filter(
            (follower) =>
              follower.membershipState !==
                FollowerMembershipState.PAYMENT_FAILED &&
              follower.membershipPlanId === membershipPlan.id
          )
        : followersData,
    [followersData, membershipPlan]
  );

  const visibleFollowers = useMemo(
    () =>
      displayAllFollowers
        ? activeFollowersData
        : activeFollowersData.filter(
            (follower) => follower.status === FollowerStatus.SUBSCRIBED
          ),
    [displayAllFollowers, activeFollowersData]
  );

  const itemsToShow: AccountData[] = useMemo(() => {
    let searchResults: AccountData[] = fetchedFollowerData;
    if (searchTerm !== "") {
      searchResults = filter(fetchedFollowerData, (user: AccountData) => {
        return containsUser(user, searchTerm);
      });
    }
    return searchResults;
  }, [fetchedFollowerData, searchTerm, containsUser]);

  const selectAllFollowers = useCallback(() => {
    addOrFilterRecipients(
      unsavedRecipients,
      setUnsavedRecipients,
      visibleFollowers.map((follower) => follower.uid)
    );
  }, [unsavedRecipients, setUnsavedRecipients, visibleFollowers]);

  const isAllSelected = useMemo(() => {
    return selectedType.type === SelectRecipientType.MEMBERSHIPS
      ? visibleFollowers.every((follower) =>
          unsavedRecipients.includes(follower.uid)
        )
      : visibleFollowers.length === unsavedRecipients.length;
  }, [selectedType.type, visibleFollowers, unsavedRecipients]);

  const renderHeader = useMemo(() => {
    return (
      <div>
        <MassTextsActionItem
          title={`Select All ${
            membershipPlan ? membershipPlan.name + " Subscribers" : ""
          }`}
          icon={
            <Icon
              icon="ion:people"
              height={32}
              style={{ color: Colors.BLUE5 }}
            />
          }
          onPress={selectAllFollowers}
          subtext={`${visibleFollowers.length} People`}
          alternateRightIcon={
            !loadedAllFollowers ? (
              <CircularProgress style={{ color: Colors.GRAY1 }} size={18} />
            ) : (
              <CustomCheckbox
                checked={isAllSelected}
                onChange={selectAllFollowers}
                sx={{ padding: 0 }}
                altColor={Colors.BLUE5}
              />
            )
          }
          disabled={!loadedAllFollowers}
          loading={!loadedAllFollowers}
        />
        <HorizontalDivider altMargin={10} />
      </div>
    );
  }, [
    isAllSelected,
    loadedAllFollowers,
    membershipPlan,
    selectAllFollowers,
    visibleFollowers,
  ]);

  return (
    <>
      <SearchBoxContainer
        value={searchTerm}
        placeholder={"Search People..."}
        onChange={(e) => {
          handleSearch(e.target.value);
        }}
        containerStyles={{ marginTop: 0, marginBottom: 14 }}
      />
      <VirtuosoList
        items={itemsToShow}
        loadMoreOnPress={loadMoreFollowerUsers}
        renderItem={(item) => {
          return (
            <MassTextsPersonItem
              user={item}
              unsavedPeople={unsavedRecipients}
              setUnsavedPeople={setUnsavedRecipients}
              excludingMode={excludingMode}
              setProfileSelected={setProfileSelected}
            />
          );
        }}
        isLoading={isFetchedFollowersLoading}
        isFinished={isFinishedFollowers}
        searchTerm={searchTerm}
        renderNonSearchEmptyView={
          <EmptySearchState mainText="No people to show" />
        }
        renderHeader={!excludingMode ? renderHeader : null}
      />
    </>
  );
};

export default SelectRecipientsFollowers;
